import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Landmark, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const chamberColor = (ch) => {
  if (ch === "House") return "bg-blue-100 text-blue-800 border-blue-200";
  if (ch === "Senate") return "bg-purple-100 text-purple-800 border-purple-200";
  if (ch === "Joint") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  return "bg-gray-100 text-gray-800 border-gray-200";
};

export default function Committees() {
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [chamberFilter, setChamberFilter] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/committees`, { timeout: 30000 })
      .then((r) => setCommittees(r.data || []))
      .catch(() => toast.error("Failed to load committees"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = committees.filter((c) => {
    if (chamberFilter !== "all" && c.chamber !== chamberFilter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group full committees at the top, subcommittees nested visually
  const topLevel = filtered.filter((c) => c.kind === "Committee");
  const subs = filtered.filter((c) => c.kind === "Subcommittee");

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start gap-3">
            <Landmark className="w-10 h-10 text-emerald-600 shrink-0 mt-1" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900" data-testid="committees-title">
                Congressional Committees
              </h1>
              <p className="text-base text-gray-600 mt-1 max-w-3xl">
                Every active committee and subcommittee in the U.S. Congress. Click one to see its
                members and the bills they've sponsored recently.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              data-testid="committees-search"
              placeholder="Search committees by name (e.g. Finance, Armed Services)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-5 text-base rounded-xl border-2"
            />
          </div>
          <Tabs value={chamberFilter} onValueChange={setChamberFilter}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="all" data-testid="committees-filter-all">All</TabsTrigger>
              <TabsTrigger value="House" data-testid="committees-filter-house">House</TabsTrigger>
              <TabsTrigger value="Senate" data-testid="committees-filter-senate">Senate</TabsTrigger>
              <TabsTrigger value="Joint" data-testid="committees-filter-joint">Joint</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-3" />
            <p className="text-gray-600">Loading committees…</p>
          </div>
        )}

        {!loading && (
          <>
            <p className="text-sm text-gray-600 mb-4" data-testid="committees-count">
              Showing <span className="font-semibold text-gray-900">{filtered.length}</span> of{" "}
              <span className="font-semibold text-gray-900">{committees.length}</span> committees
              {search ? <> matching "<em>{search}</em>"</> : null}
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topLevel.map((c) => (
                <CommitteeCard key={c.name} c={c} />
              ))}
            </div>

            {subs.length > 0 && (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-3">Subcommittees</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subs.map((c) => (
                    <CommitteeCard key={c.name} c={c} />
                  ))}
                </div>
              </>
            )}

            {filtered.length === 0 && (
              <Card className="p-8 text-center text-gray-500">No committees match your filters.</Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function CommitteeCard({ c }) {
  return (
    <Link
      to={`/committees/${encodeURIComponent(c.name)}`}
      data-testid={`committee-card-${c.name}`}
      className="block"
    >
      <Card className="p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:shadow-md transition-all h-full">
        <Badge className={`${chamberColor(c.chamber)} border px-2 py-0.5 text-xs mb-2`}>
          {c.chamber} · {c.kind}
        </Badge>
        <p className="font-semibold text-gray-900 leading-snug">{c.name}</p>
      </Card>
    </Link>
  );
}
